import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Gift, ArrowLeft, Flag, Edit, Trash2, Eye } from "lucide-react";
import MessageButton from "@/components/MessageButton";
import SaveButton from "@/components/SaveButton";
import AdminRemoveButton from "@/components/AdminRemoveButton";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setListing(false));
  }, [id]);

  if (listing === false) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Listing not found.</div>;
  if (!listing) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Loading…</div>;

  const isOwner = user?.id === listing.owner_id;

  const submitReport = async () => {
    if (!reportReason.trim()) return toast.error("Please describe the reason");
    await api.post("/reports", { target_type: "listing", target_id: listing.id, reason: reportReason, details: reportDetails });
    toast.success("Thanks for keeping the community safe. We'll review this listing.");
    setReportReason(""); setReportDetails("");
  };

  const remove = async () => {
    if (!confirm("Delete this listing?")) return;
    await api.delete(`/listings/${listing.id}`);
    toast.success("Listing deleted");
    nav("/marketplace");
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#1E3A2F] hover:text-[#C05A3D] mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8">
        <div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#F0EBDC] border border-[#E2DED0]">
            {listing.images?.[active] ? (
              <img src={imageUrl(listing.images[active])} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center font-display text-4xl text-[#C05A3D]">ABM</div>
            )}
          </div>
          {listing.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {listing.images.map((p, i) => (
                <button key={p} onClick={() => setActive(i)} className={`aspect-square rounded-lg overflow-hidden border ${i === active ? "border-[#C05A3D]" : "border-[#E2DED0]"}`}>
                  <img src={imageUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="abm-chip">{listing.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A1A1A] mt-3 leading-tight">{listing.title}</h1>
          <div className="mt-3 flex items-center gap-4">
            {listing.is_free ? (
              <span className="inline-flex items-center gap-1 font-display text-2xl text-[#1E3A2F]"><Gift className="w-5 h-5" /> Free</span>
            ) : (
              <span className="font-display text-3xl text-[#C05A3D]">${Number(listing.price || 0).toFixed(0)}</span>
            )}
            <span className="text-xs text-[#5C5C5C] uppercase tracking-widest">{listing.condition}</span>
          </div>

          <div className="mt-5 space-y-2 text-sm text-[#5C5C5C]">
            <div className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {listing.location || "Washington State"}</div>
            <div className="inline-flex items-center gap-2 ml-4"><Eye className="w-4 h-4" /> {listing.views} views</div>
          </div>

          <div className="mt-6 rounded-2xl bg-white border border-[#E2DED0] p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Posted by</div>
            <div className="font-display text-lg text-[#1E3A2F] mt-1">{listing.owner_name || "Member"}</div>
            {listing.contact_method && (
              <div className="mt-3 text-sm text-[#1A1A1A] whitespace-pre-wrap">{listing.contact_method}</div>
            )}
            {!isOwner && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <MessageButton
                  recipientId={listing.owner_id}
                  recipientName={listing.owner_name || "Member"}
                  targetType="listing"
                  targetId={listing.id}
                  targetTitle={listing.title}
                  size="sm"
                />
                <SaveButton type="listing" id={listing.id} />
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {isOwner ? (
              <>
                <Button data-testid="listing-edit-btn" className="abm-btn-pine rounded-full" onClick={() => nav(`/post/listing/${listing.id}`)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button data-testid="listing-delete-btn" variant="destructive" className="rounded-full" onClick={remove}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <Button data-testid="listing-report-btn" variant="outline" className="rounded-full">
                    <Flag className="w-4 h-4 mr-1" /> Report
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Report this listing</DialogTitle></DialogHeader>
                  <Input placeholder="Reason (e.g. scam, prohibited item)" value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-reason-input" />
                  <Textarea placeholder="More details (optional)" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} data-testid="report-details-input" />
                  <Button onClick={submitReport} className="abm-btn-primary rounded-full" data-testid="report-submit">Submit report</Button>
                </DialogContent>
              </Dialog>
            )}
            <AdminRemoveButton type="listing" id={listing.id} title={listing.title} redirectTo="/marketplace" />
          </div>
        </div>
      </div>

      <div className="mt-10 max-w-3xl">
        <h2 className="font-display text-2xl text-[#1E3A2F]">Description</h2>
        <p className="mt-3 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">{listing.description}</p>
      </div>
    </div>
  );
}
